import React from 'react';
import { useSnackbar } from 'notistack';
import { Location } from 'components/location/models/LocationModel';
import { isDefined, isEmptyString } from 'lib/typeguards';
import {
    CustomParamKeys,
    DeepLinkCustomPaths,
    DeepLinkDestinations,
    ICreateTrackingLink,
    ICustomQueryParam,
    replaceDeepLinkCustomPath,
    TrackingLinkType
} from '../trackingLinks';
import { linksApi } from '../trackingLinksApi';

interface IUseTrackingLinksFormProps {
    utmParametersEnabled: boolean;
    appLinksLimitReached: boolean;
    locations: Location[];
    isAppLinksConfigured: boolean;
    onAppLinkCreated: () => void;
    onSubmit: () => void;
    onClose: () => void;
}

export const useTrackingLinksForm = ({
    utmParametersEnabled,
    appLinksLimitReached,
    locations,
    isAppLinksConfigured,
    onAppLinkCreated,
    onSubmit,
    onClose
}: IUseTrackingLinksFormProps) => {
    const [userSelectedType, setUserSelectedType] = React.useState<TrackingLinkType>(
        TrackingLinkType.APP_LINK
    );
    const { enqueueSnackbar } = useSnackbar();
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [isDirty, setIsDirty] = React.useState(false);
    const [confirmCloseOpen, setConfirmCloseOpen] = React.useState(false);

    const selectedType = React.useMemo(
        () =>
            !isAppLinksConfigured || appLinksLimitReached ? TrackingLinkType.EXTERNAL_LINK : userSelectedType,
        [appLinksLimitReached, isAppLinksConfigured, userSelectedType]
    );

    const locationOptions = React.useMemo(
        () =>
            locations
                .filter(location => location.state === 'ACTIVE' || location.state === 'PENDING')
                .map(location => ({
                    label: location.title,
                    value: location._id
                })),
        [locations]
    );

    const handleClose = React.useCallback(() => {
        if (isDirty) {
            setConfirmCloseOpen(true);
        } else {
            onClose();
        }
    }, [isDirty, onClose]);

    const handleConfirmClose = React.useCallback(() => {
        setConfirmCloseOpen(false);
        setIsDirty(false);
        onClose();
    }, [onClose]);

    const handleCancelClose = React.useCallback(() => {
        setConfirmCloseOpen(false);
    }, []);

    const handleSelectAppLink = React.useCallback(() => {
        setUserSelectedType(TrackingLinkType.APP_LINK);
    }, []);

    const handleSelectWebLink = React.useCallback(() => {
        setUserSelectedType(TrackingLinkType.EXTERNAL_LINK);
    }, []);

    const handleSubmit = React.useCallback(
        async (values: any) => {
            setIsSubmitting(true);
            let utmParams = {};
            if (utmParametersEnabled) {
                utmParams = {
                    utmSource: values.utmSource,
                    utmMedium: values.utmMedium,
                    utmCampaign: values.utmCampaign,
                    utmContent: values.utmContent,
                    utmTerm: values.utmTerm
                };
            }
            const customParams: ICustomQueryParam[] = [];
            if (isDefined(values.tableNumber) && !isEmptyString(values.tableNumber) && utmParametersEnabled) {
                customParams.push({ key: CustomParamKeys.TABLE_NUMBER, value: values.tableNumber });
            }
            let customPath = undefined;
            if (isDefined(values.location) && !isEmptyString(values.location)) {
                if (isDefined(values.webDeepLink) && !isEmptyString(values.webDeepLink)) {
                    customPath = replaceDeepLinkCustomPath(
                        DeepLinkCustomPaths[values.webDeepLink as DeepLinkDestinations],
                        values.location
                    );
                } else {
                    customPath = replaceDeepLinkCustomPath(
                        DeepLinkCustomPaths[DeepLinkDestinations.START_OF_JOURNEY],
                        values.location
                    );
                }
            }

            const requestBody: ICreateTrackingLink = {
                type: selectedType,
                internalTitle: values.internalTitle,
                ...(selectedType === TrackingLinkType.APP_LINK && {
                    fallbackUrl: values.fallbackUrl
                }),
                ...utmParams,
                customPath,
                customQueryParameters:
                    selectedType === TrackingLinkType.EXTERNAL_LINK && customParams.length > 0
                        ? customParams
                        : undefined
            };

            try {
                const { ok, body } = await linksApi.create({ body: requestBody });
                if (!ok) {
                    throw new Error(body.message);
                }
                enqueueSnackbar('Campaign link created successfully', { variant: 'success' });
                if (selectedType === TrackingLinkType.APP_LINK) {
                    onAppLinkCreated();
                }
                onSubmit();
            } catch (error) {
                enqueueSnackbar(error.message, { variant: 'error' });
            } finally {
                setIsSubmitting(false);
            }
        },
        [utmParametersEnabled, selectedType, enqueueSnackbar, onAppLinkCreated, onSubmit]
    );

    return {
        selectedType,
        isSubmitting,
        appLinksLimitReached,
        isDirty,
        setIsDirty,
        confirmCloseOpen,
        locationOptions,
        handleClose,
        handleConfirmClose,
        handleCancelClose,
        handleSelectAppLink,
        handleSelectWebLink,
        handleSubmit
    };
};
